import React, { useMemo } from 'react'
import { Row, Col, Card, Table, Tag, Timeline } from 'antd'
import { useSelector } from 'react-redux'
import dayjs from 'dayjs'
import { formatCurrency } from '../../../../../utils/formatters'
import styles from './ExpiryTracking.module.css'

// Define color constants to ensure consistency
const EXPIRY_COLORS = {
  critical: 'red', // ≤ 30 days
  warning: 'orange', // 31-60 days
  attention: 'blue', // 61-90 days
  safe: 'green' // > 90 days
}

const ExpiryTrackingTab = ({ dateRange }) => {
  const inventoryItems = useSelector((state) => state.inventory.items)

  const expiryData = useMemo(() => {
    return inventoryItems
      .filter((item) => item.expiryDate)
      .map((item) => {
        const daysUntilExpiry = dayjs(item.expiryDate).diff(dayjs(), 'day')

        let expiryStatus = 'safe'
        if (daysUntilExpiry <= 30) {
          expiryStatus = 'critical'
        } else if (daysUntilExpiry <= 60) {
          expiryStatus = 'warning'
        } else if (daysUntilExpiry <= 90) {
          expiryStatus = 'attention'
        }

        return {
          ...item,
          daysUntilExpiry,
          expiryStatus
        }
      })
      .filter((item) => item.daysUntilExpiry >= 0)
      .sort((a, b) => a.daysUntilExpiry - b.daysUntilExpiry)
  }, [inventoryItems])

  const timelineItems = useMemo(() => {
    const groups = {
      critical: expiryData.filter((item) => item.expiryStatus === 'critical'),
      warning: expiryData.filter((item) => item.expiryStatus === 'warning'),
      attention: expiryData.filter((item) => item.expiryStatus === 'attention')
    }

    return [
      {
        color: EXPIRY_COLORS.critical,
        children: (
          <>
            <Tag color={EXPIRY_COLORS.critical}>Critical (≤ 30 days)</Tag>
            <div>{groups.critical.length} items</div>
          </>
        )
      },
      {
        color: EXPIRY_COLORS.warning,
        children: (
          <>
            <Tag color={EXPIRY_COLORS.warning}>Warning (31-60 days)</Tag>
            <div>{groups.warning.length} items</div>
          </>
        )
      },
      {
        color: EXPIRY_COLORS.attention,
        children: (
          <>
            <Tag color={EXPIRY_COLORS.attention}>Attention (61-90 days)</Tag>
            <div>{groups.attention.length} items</div>
          </>
        )
      }
    ]
  }, [expiryData])

  const columns = [
    {
      title: 'Item Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name)
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      sorter: (a, b) => a.category.localeCompare(b.category)
    },
    {
      title: 'Expiry Date',
      dataIndex: 'expiryDate',
      key: 'expiryDate',
      render: (date) => dayjs(date).format('MMM D, YYYY'),
      sorter: (a, b) => dayjs(a.expiryDate).unix() - dayjs(b.expiryDate).unix()
    },
    {
      title: 'Current Stock',
      dataIndex: 'currentStock',
      key: 'currentStock',
      sorter: (a, b) => a.currentStock - b.currentStock
    },
    {
      title: 'Days Until Expiry',
      key: 'daysUntilExpiry',
      dataIndex: 'daysUntilExpiry',
      render: (days, record) => {
        // Only apply color-coded tags for items within our monitoring ranges (0-90 days)
        // Otherwise use default/green tag
        return <Tag color={EXPIRY_COLORS[record.expiryStatus]}>{days} days</Tag>
      },
      sorter: (a, b) => a.daysUntilExpiry - b.daysUntilExpiry
    },
    {
      title: 'Value at Risk',
      key: 'valueAtRisk',
      render: (_, record) => formatCurrency(record.currentStock * record.unitPrice, true),
      sorter: (a, b) => a.currentStock * a.unitPrice - b.currentStock * b.unitPrice
    }
  ]

  const filteredExpiryData = useMemo(() => {
    // For the table, show items expiring within 90 days first, then the rest
    if (dateRange && dateRange[0] && dateRange[1]) {
      // If date range is applied, show all items in the range
      return expiryData
    }

    // Sort items so that items expiring within 90 days appear first
    return [...expiryData].sort((a, b) => {
      // First sort by monitoring priority (critical, warning, attention, then safe)
      const priorityOrder = { critical: 1, warning: 2, attention: 3, safe: 4 }
      const priorityDiff = priorityOrder[a.expiryStatus] - priorityOrder[b.expiryStatus]

      if (priorityDiff !== 0) return priorityDiff

      // If they're in the same priority group, sort by days until expiry
      return a.daysUntilExpiry - b.daysUntilExpiry
    })
  }, [expiryData, dateRange])

  return (
    <div className={styles.expiryTrackingContainer}>
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={8}>
          <Card title='Expiry Timeline' className={styles.expiryTimelineCard}>
            <Timeline items={timelineItems} />
          </Card>
        </Col>
        <Col xs={24} lg={16}>
          <Card title='Expiring Items' className={styles.expiryTableCard}>
            <Table
              dataSource={filteredExpiryData}
              columns={columns}
              rowKey='id'
              pagination={false}
              scroll={{ x: true }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  )
}

export default ExpiryTrackingTab
